import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionModal from "../../app/SectionModal";
import { useModalHeadSlot } from "../../shared/modal/Modal";
import EggBalanceChip from "../../shared/shop/EggBalanceChip";
import { setEggBalance, eggBalanceNow } from "../../shared/shop/useEggBalance";
import { demoUnopened, useUnopenedAchievements, type Unopened } from "../../shared/achievements-open";
import { OpenBanner, OpenableCard } from "../profile/OpenAchievements";
import { LevelHexes, AchProgress } from "../profile/ProfilePage";
import type { AchievementView } from "../../shared/rating/achievements";
import styles from "../profile/ProfilePage.module.scss";

// The profile's achievements waiting to be opened, with made-up ones and a
// made-up balance: the cards, the band and the eggs flying into the count.
// "few": 7 eggs in all (each lands with its own note); "many": 34 (one stream, one sound)
const SETS: Record<string, Unopened[]> = {
  few: [{ id: "wins", level: 1, eggs: 2 }, { id: "gaps", level: 1, eggs: 2 }, { id: "crashes", level: 1, eggs: 3 }],
  many: [{ id: "wins", level: 2, eggs: 5 }, { id: "gaps", level: 1, eggs: 3 }, { id: "royale-regular", level: 1, eggs: 10 }, { id: "crashes", level: 3, eggs: 16 }],
};
const CARDS = [
  { id: "wins", icon: "🏁", name: "Round Robin I", desc: "Next: Win 50 rounds.", value: 13, to: 50, nextEggs: 3 },
  { id: "gaps", icon: "🧵", name: "Needle Threader I", desc: "Next: Thread 50 gaps.", value: 11, to: 50, nextEggs: 3 },
  { id: "royale-regular", icon: "👑", name: "Royale Regular I", desc: "Next: Play 10 Royales.", value: 4, to: 10, nextEggs: 5 },
  { id: "crashes", icon: "🧱", name: "Wall Builder I", desc: "Next: Make 50 worms crash into your trail.", value: 12, to: 50, nextEggs: 3 },
];
const view = (c: (typeof CARDS)[number]) => ({ from: 0, value: c.value, to: c.to, done: false, nextEggs: c.nextEggs }) as unknown as AchievementView;

function Lab({ embedded }: { embedded?: boolean }) {
  const unopened = useUnopenedAchievements();
  const [eggs, setEggs] = useState(120);
  useEffect(() => {
    setEggBalance(120);
    demoUnopened(SETS.few);
    let raf = 0;
    const loop = () => { const v = eggBalanceNow(); if (v != null) setEggs(v); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  useModalHeadSlot(<EggBalanceChip value={eggs} />);
  return (
    <div style={{ padding: 16 }}>
      {/* (outside the modal, in the progress lab: the egg count on its own line) */}
      {embedded && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}><EggBalanceChip value={eggs} /></div>}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" data-set="few" onClick={() => demoUnopened(SETS.few)}>Reset: 7 eggs</button>
        <button type="button" data-set="many" onClick={() => demoUnopened(SETS.many)}>Reset: 34 eggs</button>
      </div>
      <OpenBanner unopened={unopened} />
      <div className={styles.achGrid}>
        {CARDS.map((c) => (
          <OpenableCard key={c.id} items={unopened.filter((u) => u.id === c.id)} className={`${styles.ach} ${styles.achReached} ${unopened.some((u) => u.id === c.id) ? styles.achWaiting : ""}`} style={{ "--lv": "#d9a531" } as React.CSSProperties}>
            <span className={styles.achMedal} aria-hidden="true" data-medal=""><span className={styles.achIcon}>{c.icon}</span></span>
            <div className={styles.achBody}>
              <span className={styles.achTop}><span className={styles.achName}>{c.name}</span><LevelHexes levels={5} level={1} /></span>
              <span className={styles.achDesc}>{c.desc}</span>
              <AchProgress a={view(c)} />
            </div>
          </OpenableCard>
        ))}
      </div>
    </div>
  );
}

export default function OpenAchievementsLab({ embedded }: { embedded?: boolean }) {
  const navigate = useNavigate();
  if (embedded) return <Lab embedded />;
  return <SectionModal title="Profile" tabbed eggs onClose={() => navigate("/")}><Lab /></SectionModal>;
}
